import { useCallback } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import type { AccountInfo } from "@azure/msal-browser";
import { useAuthConfig } from "./authContext";
import { clearLoginHint } from "./loginHintStore";

export interface UseAuthResult {
    readonly isAuthenticated: boolean;
    readonly account: AccountInfo | null;
    signIn(): Promise<void>;
    signOut(): Promise<void>;
}

/**
 * Minimal authentication surface exposed to compositions.
 *
 * This hook deliberately returns the four things compositions need and nothing
 * else — it does not leak the MSAL instance, the raw accounts array, or the
 * config. Compositions therefore cannot accidentally couple to MSAL internals.
 */
export function useAuth(): UseAuthResult {
    const { instance, accounts } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const config = useAuthConfig();

    const account: AccountInfo | null = instance.getActiveAccount() ?? accounts[0] ?? null;

    const signIn = useCallback(async (): Promise<void> => {
        await instance.loginRedirect({ scopes: [config.msal.apiScope] });
    }, [instance, config.msal.apiScope]);

    const signOut = useCallback(async (): Promise<void> => {
        // Forget the silent-SSO target first: an explicit sign-out must not be
        // undone by a silent renewal on the next cold tab.
        clearLoginHint();
        await instance.logoutRedirect();
    }, [instance]);

    return {
        isAuthenticated,
        account,
        signIn,
        signOut,
    };
}
